import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { isActiveRevendedor } from '@/utils/revendedores';
import SearchableCombobox from '@/components/ui/searchable-combobox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewManualOfferDialog = ({
  open,
  onOpenChange,
  onSuccess,
  vehiculos = {},
  revendedores = {},
  preselectedVehiculo = null
}) => {
  const [newOffer, setNewOffer] = useState({
    vehiculoId: preselectedVehiculo ? preselectedVehiculo.id : '',
    revendedorId: '',
    monto: '',
  });

  useEffect(() => {
    if (open) {
      setNewOffer({
        vehiculoId: preselectedVehiculo ? preselectedVehiculo.id : '',
        revendedorId: '',
        monto: '',
      });
    }
  }, [open, preselectedVehiculo]);

  const handleCreateOffer = async () => {
    try {
      if (!newOffer.vehiculoId || !newOffer.revendedorId || !newOffer.monto) {
        toast.error('Completa todos los campos');
        return;
      }

      await axios.post(`${API}/ofertas`, {
        vehiculoId: newOffer.vehiculoId,
        revendedorId: newOffer.revendedorId,
        monto: parseFloat(newOffer.monto),
        esManual: true,
      });

      toast.success('Oferta creada manualmente');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error creating local offer:', error);
      toast.error('Error al crear oferta');
    }
  };

  const vehiculoOptions = Object.values(vehiculos)
    .filter(v => ['Enviada', 'En espera'].includes(v.estadoPublicacion) && !v.deletedAt)
    .map(v => ({
      value: v.id,
      label: `${v.marca} ${v.modelo} ${v.anio}`,
      searchValue: `${v.marca} ${v.modelo} ${v.anio} ${v.id}`,
    }));

  const revendedorOptions = Object.values(revendedores)
    .filter(isActiveRevendedor)
    .map(r => ({
      value: r.id,
      label: `${r.nombre} - ${r.empresa}`,
      searchValue: `${r.nombre} ${r.empresa} ${r.id}`,
    }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cargar Oferta Manual</DialogTitle>
          <DialogDescription>
            Registra una oferta recibida de forma manual
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="vehiculo">Vehículo</Label>
            {preselectedVehiculo ? (
              <div className="p-3 bg-muted rounded-md border text-sm font-medium">
                {preselectedVehiculo.marca} {preselectedVehiculo.modelo} {preselectedVehiculo.anio}
              </div>
            ) : (
              <SearchableCombobox
                value={newOffer.vehiculoId}
                onSelect={(val) => setNewOffer({ ...newOffer, vehiculoId: val })}
                options={vehiculoOptions}
                placeholder="Seleccionar vehículo..."
                searchPlaceholder="Buscar vehículo..."
                emptyText="No se encontraron vehículos."
                contentClassName="w-[400px] sm:w-[500px]"
              />
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="revendedor">Revendedor</Label>
            <SearchableCombobox
              value={newOffer.revendedorId}
              onSelect={(val) => setNewOffer({ ...newOffer, revendedorId: val })}
              options={revendedorOptions}
              placeholder="Seleccionar revendedor..."
              searchPlaceholder="Buscar revendedor..."
              emptyText="No se encontró revendedor."
              contentClassName="w-[400px] sm:w-[500px]"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="monto">Monto (USD)</Label>
            <Input
              id="monto"
              type="number"
              placeholder="15000"
              value={newOffer.monto}
              onChange={(e) => setNewOffer({ ...newOffer,  monto: e.target.value })}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleCreateOffer}>Crear Oferta</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewManualOfferDialog;
